/**
 * CookieConsent.jsx - GDPR Compliant Cookie Consent Component
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsService } from '../services/analytics';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false); // Will be controlled by useEffect
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice (use same key as analytics service)
    const consentStatus = localStorage.getItem('analytics_consent');
    
    if (!consentStatus || consentStatus === 'null') {
      // Show banner after a small delay to ensure DOM is ready
      setTimeout(() => {
        setShowBanner(true);
      }, 100);
    } else if (consentStatus === 'true') {
      // User already accepted, keep banner hidden
      setShowBanner(false);
    } else {
      // User previously declined or other status - show banner to allow changing mind
      setTimeout(() => {
        setShowBanner(true);
      }, 100);
    }
  }, []);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      // Grant consent to analytics service
      analyticsService.grantConsent();
      
      // Hide banner
      setShowBanner(false);
      
      console.log('✅ Cookie consent accepted - Analytics enabled');
    } catch (error) {
      console.error('Error accepting cookies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    setIsLoading(true);
    try {
      // Deny consent to analytics service
      analyticsService.denyConsent();
      
      // Hide banner
      setShowBanner(false);
      
      console.log('❌ Cookie consent declined - Analytics disabled');
    } catch (error) {
      console.error('Error declining cookies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-2xl" style={{zIndex: 99999}}>
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Content */}
          <div className="flex-1 text-sm text-gray-700">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">
                  🍪 We use cookies to improve your experience
                </p>
                <p className="text-gray-600">
                  We use analytics cookies to understand how you use our site and improve your experience.
                  <Link to="/cookies" className="text-blue-600 hover:text-blue-800 ml-1">
                    Learn more
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              {isLoading ? 'Processing...' : 'Decline'}
            </button>
            
            <button
              onClick={handleAccept}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Processing...' : 'Accept All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;